import { defineStore, storeToRefs } from 'pinia';
import type { FetchError } from 'ofetch';
import { Stock } from '../types/stock';
import { useTrackingStore } from './tracking';
import { useOrderStore } from './order';
import { useStoresStore } from './stores';
import { cartMachine, statuses, events } from './state-machines/cartMachine';
import { DeliveryId, useDeliveryStore } from './delivery';
import { useBrandStore } from './brand';
import { useUiStore } from './ui';
import {
  DELIVERY_ID,
  DELIVERY_EXPRESS,
  STORE_EXPRESS,
  EXPRESS_DELIVERIES,
  STORE_DELIVERIES,
  STANDARD_DELIVERIES,
} from '@/config/delivery-config';
import gql from '@/gql/cart';
import type { Price, PurchaseConstraints, Variant, VariantWithQuantity } from '@/types/products';
import type { UpdateItemResponse } from '@/plugins/services/cart/types';
import { OrderDeliveryDetails } from '~/types/order';
import { RequiresChange } from '~/types/cart';

export type CartItem = {
  name: string;
  cartItemId: number;
  cartId: string;
  variant?: Variant; // This needs to be optional since it's deleted in clearCartItems
  deliveryId: DeliveryId;
  requestedDeliveryDate: string;
  articleNumber: string;
  quantity: number;
  label: string;
  price: Price;
  images: string[];
  stock?: Stock;
  variantSlug?: string;
  discontinued?: boolean;
  purchaseConstraints: PurchaseConstraints[];
};

type CleanedDeliveryMethod = {
  deliveryId: string;
  addressId?: string;
  readOnly?: boolean;
  countryCode?: string;
  label?: string;
  name?: string;
  recipientName?: string;
  address1?: string;
  address2?: string;
  address3?: string;
  postalCode?: string;
  postalAddress?: string;
  created?: number;
  requstedDeliveryDate?: Date;
};

type FetchedCart = {
  cartId: number;
  cartItems: CartItem[];
  deliveryIdsInOrder: string[];
};

const helpers = {
  cleanCartItems: (cartItems: CartItem[]): CartItem[] => {
    const cleanedCartItems = clone<CartItem[]>(cartItems);
    return cleanedCartItems.map((item) => {
      const variantData = item.variant;
      delete item.variant;
      return { ...item, ...variantData };
    });
  },
};

export const useCartStore = defineStore('cart', () => {
  const cartId = ref<number | undefined>(undefined);
  const cartStatus = ref<string>(statuses.noCart);
  const hasLoadedInitialData = ref<boolean>(false);
  const cartItems = ref<CartItem[]>([]);
  const quickOrderVariants = ref<VariantWithQuantity[]>([]);
  const itemsAddedNotification = ref<{ items: CartItem[]; timer: number | undefined }>({
    items: [],
    timer: undefined,
  });

  const setCartId = (id: number | undefined) => (cartId.value = id);

  const setCartStatus = (event: string): void => {
    cartStatus.value = cartMachine.transition(cartStatus.value, event);
    if (cartStatus.value === statuses.noCart) {
      hasLoadedInitialData.value = false;
    }
  };

  const setCartItems = (items: CartItem[]): void => {
    cartItems.value = items;
  };

  const setQuickOrderVariants = (variants: VariantWithQuantity[]): void => {
    quickOrderVariants.value = variants;
  };

  const rectifyDeliveryDate = async (): Promise<void> => {
    if (hasLoadedInitialData.value === false) return;

    const now = new Date();
    const today = now.toISOString().slice(0, 10);
    let hasDifferentDeliveryDate = false;

    // Check each cart items requested delivery date, if it is not today reset the delivery date
    for (const cartItem of cartItems.value) {
      if (cartItem.requestedDeliveryDate !== today) {
        hasDifferentDeliveryDate = true;
        break;
      }
    }

    if (!hasDifferentDeliveryDate) return;

    const { showMessage } = useUiStore();
    const { $cartService, $i18n } = useNuxtApp();

    const request = {
      cartId: cartId.value!,
      date: now.toISOString(),
    };

    setCartStatus(events.UPDATE);
    const { data, error } = await $cartService.updateAllRequestedDeliveryDates(request);
    if (error || !data)
      return showMessage({ text: $i18n.t('errors.occured') + ' ' + $i18n.t('errors.update_delivery_date') });

    // Update the requested delivery date for the cart items
    for (const item of data) {
      const cartItem = cartItems.value.find((cartItem) => cartItem.cartItemId === item.cartItemId);
      if (cartItem) cartItem.requestedDeliveryDate = item.requestedDeliveryDate;
    }

    setCartStatus(events.SUCCESS);
  };

  const updateRequestedDeliveryDate = async ({
    date,
    deliveryId,
  }: {
    date: Date;
    deliveryId: string;
  }): Promise<void> => {
    const { showMessage } = useUiStore();
    const { $cartService, $i18n } = useNuxtApp();
    const request = {
      cartId: cartId.value!,
      deliveryId,
      date: date.toISOString().length > 0 ? date.toISOString() : '',
    };

    setCartStatus(events.UPDATE);
    const { error, data } = await $cartService.updateRequestedDeliveryDate(request);
    if (error || !data)
      return showMessage({ text: $i18n.t('errors.occured') + ' ' + $i18n.t('errors.update_delivery_date') });

    // Update the requested delivery date for the cart items
    for (const item of data) {
      const cartItem = cartItems.value.find((cartItem) => cartItem.cartItemId === item.cartItemId);
      if (cartItem) cartItem.requestedDeliveryDate = item.requestedDeliveryDate;
    }
    setCartStatus(events.SUCCESS);
  };

  const transferCart = async (currentCartId: number): Promise<void> => {
    const { $cartService, $i18n } = useNuxtApp();
    const { showMessage, addToastMessage } = useUiStore();

    await fetchCart();

    const { error } = await $cartService.transferCart({
      sourceId: currentCartId,
      destId: cartId.value!,
    });

    if (error) {
      showMessage({ text: $i18n.t('errors.transfer_cart') });
      return;
    }

    fetchCart();

    addToastMessage({ text: $i18n.t('user.change_project_items_moved') });
  };

  const fetchCart = async (): Promise<void> => {
    if (process.server) return;
    const { $i18n } = useNuxtApp();
    setCartStatus(events.UPDATE);

    try {
      const cart = await fetchCartWithStockProductPrice();
      cartId.value = cart.cartId;

      setCartStatus(events.SUCCESS);
    } catch (err) {
      setCartStatus(events.ERROR);
      showErrorMessage($i18n.t('errors.cart_service_not_available'));
    } finally {
      hasLoadedInitialData.value = true;
    }
  };

  const fetchCartWithStockProductPrice = async (payload: { useCache?: boolean } = {}): Promise<FetchedCart> => {
    const { useCache } = payload;
    const { $apiGateway } = useNuxtApp();
    const deliveryStore = useDeliveryStore();
    const { selectedLocations, deliveryOptions, getStockLocationsList } = storeToRefs(deliveryStore);

    const { getZipCode, setActiveDeliveryIdsInOrder } = deliveryStore;

    const stockLocationsInput: {
      [key: string]: {
        locationIds: string[];
        destination?: {
          postalCode: string;
        };
      };
    } = {};

    for (const deliveryId in selectedLocations.value) {
      stockLocationsInput[deliveryId] = { locationIds: getStockLocationsList.value[deliveryId] };

      const config = deliveryOptions.value.find((option: { id: string }) => option.id === deliveryId) as
        | {
            useDestination?: boolean;
          }
        | undefined;
      const postalCode = getZipCode(deliveryId).value;
      if (config?.useDestination && postalCode) {
        stockLocationsInput[deliveryId].destination = {
          postalCode,
        };
      }
    }

    const { data, error } = await $apiGateway.post<{
      data: { data: { cart: FetchedCart } };
      error: FetchError;
    }>('/graphql', {
      query: gql.getCartWithVariants,
      variables: {
        stockInput: { deliveryOptions: stockLocationsInput },
        useCache,
        completeOrderPrice: true,
        locationId: stockLocationsInput.EXH?.locationIds.filter((id) => id !== 'VIRTUAL')[0],
      },
    });

    if (error) throw error;

    const cart = data?.data?.cart;

    if (!cart) throw new Error('No cart data returned from graphql');

    const items = helpers.cleanCartItems(cart.cartItems).map((variant) => {
      return {
        ...variant,
      };
    });

    setActiveDeliveryIdsInOrder(cart.deliveryIdsInOrder);
    setCartItems(items);

    return cart;
  };

  /* Only used for articles with common delivery method */
  const fetchVariants = async (items: CartItem[], query = {}): Promise<Variant[]> => {
    let res: Variant[] = [];
    const articleList: string[] = [];
    const articlesAndQuantityList: { articleNumber: string; quantity: number }[] = [];
    const deliveryId = items[0].deliveryId;
    const requestedDeliveryDate = items[0].requestedDeliveryDate;
    const { getStockLocationsList, getZipCode } = useDeliveryStore();
    const { deliveryOptions } = storeToRefs(useDeliveryStore());
    const { $apiGateway } = useNuxtApp();

    items.forEach(({ articleNumber, quantity }) => {
      articleList.push(articleNumber);
      articlesAndQuantityList.push({ articleNumber, quantity });
    });

    const stockInput: {
      articles: { articleNumber: string; quantity: number }[];
      locationIds: string[];
      requestedDeliveryDate?: string;
      destination?: { postalCode: string };
    } = {
      articles: articlesAndQuantityList,
      locationIds: getStockLocationsList[deliveryId],
    };
    const config = deliveryOptions?.value.find((option: { id: string }) => option.id === deliveryId) as
      | {
          useDestination?: boolean;
        }
      | undefined;
    const postalCode = getZipCode(deliveryId).value;
    if (config?.useDestination && postalCode) {
      stockInput.destination = {
        postalCode,
      };
    }
    if (requestedDeliveryDate) stockInput.requestedDeliveryDate = requestedDeliveryDate;

    try {
      const { data, error } = await $apiGateway.post<{ data: { data: { variants: Variant[] } }; error: FetchError }>(
        '/graphql',
        {
          query: gql.getVariants,
          variables: {
            articleNumbers: articleList,
            deliveryId,
            stockInput,
            locationId: stockInput.locationIds.filter((id) => id !== 'VIRTUAL')[0],
            ...query,
          },
        },
      );
      if (error) throw error;
      res = data?.data?.variants || [];
    } catch (err) {
      res = [];
    }

    return res;
  };

  const addItemToCart = (item: CartItem, trackingPosition: string): void => {
    addItemsToCart([item], trackingPosition);
  };

  const addItemsToCart = async (items: CartItem[], trackingPosition: string): Promise<void> => {
    const deliveryStore = useDeliveryStore();
    const { getSelectedDeliveryId } = storeToRefs(deliveryStore);
    const { activateNewDeliveryIdInOrder } = deliveryStore;
    const { addToCart } = useTrackingStore();

    setCartStatus(events.UPDATE);

    if (!getSelectedDeliveryId.value) return showErrorMessage('Vänligen välj ett leveranssätt.');

    const { $i18n, $cartService, $tracking, $router } = useNuxtApp();
    const { data, error } = await $cartService.addItemsToCart({
      cartId: cartId.value!,
      items: items.map((item) => ({
        articleNumber: item.articleNumber,
        quantity: Math.abs(+item.quantity),
        deliveryId: getSelectedDeliveryId.value as string,
        label: item.label,
      })),
    });

    if (error || !data) {
      showErrorMessage($i18n.t('errors.cart_add_product'));
    }

    const { cartItems: addedItems, errors: erroredItems } = data!;

    if (erroredItems.length) {
      showErrorMessage(
        $i18n.t('errors.cart_add_products', { products: erroredItems.map((e: { id: string }) => e.id).join(', ') }),
      );
    }

    if (addedItems.length) {
      const variants = await fetchVariants(addedItems);
      const extendedItems = addedItems.map((cartItem, index) => ({ ...cartItem, ...variants[index] }));
      setCartItems([...cartItems.value, ...extendedItems]);
      activateNewDeliveryIdInOrder(getSelectedDeliveryId.value);

      addItemsAddedNotification(extendedItems);
      setCartStatus(events.SUCCESS);
      $tracking.emit('addToCart', {
        items: extendedItems,
        pageType: $router.currentRoute.value.name,
        list: trackingPosition,
      });
      for (const item of extendedItems) {
        addToCart(item);
      }
    } else {
      setCartStatus(events.ERROR);
    }
  };

  const addItemsAddedNotification = (itemsAdded: CartItem[]): void => {
    /* Clear active timeout */
    clearTimeout(itemsAddedNotification.value.timer);
    setTimeout(() => {
      itemsAddedNotification.value = { items: [], timer: undefined };
    }, 3000);

    itemsAddedNotification.value = { items: itemsAdded, timer: undefined };
  };

  const loadXLSfile = async (formData: FormData): Promise<void> => {
    const deliveryStore = useDeliveryStore();
    const { getSelectedDeliveryId } = storeToRefs(deliveryStore);
    const { activateNewDeliveryIdInOrder } = deliveryStore;
    const { showMessage } = useUiStore();
    const { $i18n, $cartService, $tracking, $router } = useNuxtApp();

    formData.append('deliveryId', getSelectedDeliveryId.value!);

    try {
      setCartStatus(events.UPDATE);
      const payload = {
        cartId: cartId.value!,
        formData,
      };

      const { data, error } = await $cartService.postXLS(payload);

      if (error || !data) {
        showErrorMessage($i18n.t('errors.cart_uploads_products'));
        return;
      }

      const { cartItems: items, errors } = data;

      if (items.length) {
        const {
          public: { gtm },
        } = useRuntimeConfig();

        const variants = items && (await fetchVariants(items));
        const extendedItems = items.map((cartItem, index) => ({ ...cartItem, ...variants[index] }));
        setCartItems([...cartItems.value, ...extendedItems]);
        activateNewDeliveryIdInOrder(items[0].deliveryId);

        $tracking.emit('addToCart', {
          items: extendedItems,
          pageType: $router.currentRoute.value.name,
          list: gtm.ADD_TO_CART_FROM_FILE_MODAL,
        });
      }

      if (errors.length) {
        if (errors[0].id === 'CART_LIMIT') {
          showMessage({
            text: $i18n.t('cart.cart_limit_exceeded'),
          });
        } else {
          showMessage({
            text: $i18n.t('cart.wrong_import') + errors.map((error: { id: string }) => error.id).join(', '),
          });
        }
      }
      setCartStatus(events.SUCCESS);
    } catch (error) {
      setCartStatus(events.ERROR);
      showErrorMessage($i18n.t('errors.cart_uploads_products'));
    }
  };

  const getXLSX = async (itemIds: string[]): Promise<{ data: string; name: string } | undefined> => {
    const { $cartService, $i18n } = useNuxtApp();

    const { data: xlsxData, error } = await $cartService.getXLSX({
      cartId: cartId.value!,
      itemIds,
    });

    if (!xlsxData?.data || error) {
      showErrorMessage($i18n.t('errors.cart_generate_file'));
    }

    return xlsxData;
  };

  const removeSelectedItems = async (itemIds: number[]): Promise<void> => {
    const { $cartService, $i18n, $tracking, $router } = useNuxtApp();

    setCartStatus(events.UPDATE);

    const { error } = await $cartService.deleteItems({
      cartId: cartId.value!,
      itemIds,
    });

    if (error) {
      showErrorMessage($i18n.t('errors.cart_remove_products'));
      return;
    }

    const newCartItems = cartItems.value.filter((item) => {
      const included = itemIds.includes(item.cartItemId);
      return !included;
    });

    const selectedCartItems = cartItems.value.filter((item) => {
      return itemIds.includes(item.cartItemId);
    });

    $tracking.emit('removeFromCart', { items: selectedCartItems, pageType: $router.currentRoute.value.name });

    setCartItems(newCartItems);
    refreshActiveDeliveryIds();
    setCartStatus(events.SUCCESS);
  };

  const removeItem = async (item: CartItem | { cartItemId: number }): Promise<void> => {
    if (getCartIsMutating.value) return;

    const { $cartService, $i18n, $tracking, $router } = useNuxtApp();
    setCartStatus(events.UPDATE);
    const { error } = await $cartService.deleteItem({
      cartId: cartId.value!,
      itemId: item.cartItemId,
    });

    if (error) {
      showErrorMessage($i18n.t('errors.cart_remove_products'));
      return;
    }

    const newCartItems = cartItems.value.filter((cartItem) => {
      if (cartItem.cartItemId === item.cartItemId) {
        $tracking.emit('removeFromCart', { items: [cartItem], pageType: $router.currentRoute.value.name });
      }
      return cartItem.cartItemId !== item.cartItemId;
    });

    setCartItems(newCartItems);
    refreshActiveDeliveryIds();
    setCartStatus(events.SUCCESS);
  };

  const updateItem = (item: CartItem, trackingPosition: string): void => {
    setCartStatus(events.UPDATE);
    debounceUpdateItemInCartService(item, trackingPosition);
  };

  const debounceUpdateItemInCartService = debounce(async function (item: CartItem, trackingPosition: string) {
    await updateItemInCartService(item, trackingPosition);
  }, 500);

  const updateItemInCartService = async (cartItem: CartItem, trackingPosition: string): Promise<void> => {
    const { $cartService, $i18n, $tracking, $router } = useNuxtApp();
    const { activateNewDeliveryIdInOrder } = useDeliveryStore();
    try {
      const promises: [UpdateItemResponse, Promise<Variant[] | any>?] = [
        $cartService.updateItem({
          cartId: cartId.value!,
          quantity: cartItem.quantity,
          deliveryId: cartItem.deliveryId,
          label: cartItem.label === null ? undefined : cartItem.label,
          itemId: cartItem.cartItemId,
        }),
      ];

      if (cartItem.price.hasBracketPrices) {
        await promises[0];
        await fetchCartWithStockProductPrice();
        await Promise.all(promises);

        setCartStatus(events.SUCCESS);
        return;
      }

      const itemToUpdate = getCartItemsObject.value[cartItem.cartItemId];
      if (cartItem.deliveryId) {
        promises.push(
          fetchVariants([
            {
              ...cartItem,
              articleNumber: itemToUpdate.articleNumber,
              requestedDeliveryDate: itemToUpdate.requestedDeliveryDate,
            },
          ]),
        );
      }

      const [{ data: updatedCartItem, error: cartServiceError }, variants] = await Promise.all(promises);

      if (cartServiceError) {
        showErrorMessage($i18n.t('errors.cart_update_product', { product: cartItem.articleNumber }));
        return;
      }

      const newCartItem = variants?.length ? { ...updatedCartItem, ...variants[0] } : updatedCartItem!;

      setCartItems(
        cartItems.value.map((item: any) => {
          if (item.cartItemId === newCartItem.cartItemId) return { ...item, ...newCartItem };
          return item;
        }),
      );

      if (cartItem.deliveryId) {
        refreshActiveDeliveryIds();
        activateNewDeliveryIdInOrder(cartItem.deliveryId);
      }

      if (cartItem.quantity !== itemToUpdate.quantity) {
        const event = itemToUpdate.quantity < cartItem.quantity ? 'addToCart' : 'removeFromCart';
        $tracking.emit(event, {
          items: [{ ...itemToUpdate, quantity: Math.abs(itemToUpdate.quantity - cartItem.quantity) }],
          pageType: $router.currentRoute.value.name,
          list: trackingPosition,
        });
      }
      setCartStatus(events.SUCCESS);
    } catch (error) {
      setCartStatus(events.ERROR);
      showErrorMessage($i18n.t('errors.cart_update_product', { product: cartItem?.articleNumber }));
    }
  };

  const updateItems = async (items: CartItem[]): Promise<void> => {
    const { $cartService, $i18n } = useNuxtApp();
    const { activateNewDeliveryIdInOrder } = useDeliveryStore();

    setCartStatus(events.UPDATE);

    const { data, error } = await $cartService.updateItems({
      cartId: cartId.value!,
      items: items.map((item) => ({
        itemId: item.cartItemId,
        deliveryId: item.deliveryId,
        quantity: Math.abs(+item.quantity),
        label: item.label || '',
      })),
    });

    if (error || !data?.cartItems.length) {
      showErrorMessage($i18n.t('errors.cart_update_products'));
      return;
    }

    const { cartItems: updatedItems, errors } = data;

    if (errors.length) {
      showErrorMessage(
        $i18n.t('errors.cart_update_products', { products: errors.map((e: { id: string }) => e.id).join(', ') }),
      );
      return;
    }

    if (items.some((item) => item.price.hasBracketPrices)) {
      await fetchCartWithStockProductPrice();
    } else {
      const variants = await fetchVariants(updatedItems);
      const extendedItems = updatedItems.map((cartItem: CartItem, index: number) => ({
        ...cartItem,
        ...variants[index],
      }));

      setCartItems([
        ...cartItems.value.map(
          (item) => extendedItems.find((eItem: CartItem) => eItem.cartItemId === item.cartItemId) ?? item,
        ),
      ]);

      // Loop over each of the cart items delivery ids and activate the new delivery id
      const deliveryIds = updatedItems.reduce((set: Set<string>, curr: CartItem) => {
        if (curr.deliveryId) set.add(curr.deliveryId);
        return set;
      }, new Set());

      if (deliveryIds.size) {
        refreshActiveDeliveryIds();
        for (const deliveryId of deliveryIds) {
          activateNewDeliveryIdInOrder(deliveryId);
        }
      }
    }

    setCartStatus(events.SUCCESS);
  };

  const refreshActiveDeliveryIds = async (): Promise<void> => {
    const { $apiGateway } = useNuxtApp();
    const { setActiveDeliveryIdsInOrder } = useDeliveryStore();

    const { data, error } = await $apiGateway.post<{
      data: { data: { cart?: { deliveryIdsInOrder: string[] } } };
      error: FetchError;
    }>('/graphql', { query: gql.activeDeliveryIdsInCart });

    if (error) throw error;

    setActiveDeliveryIdsInOrder(data.data.cart?.deliveryIdsInOrder || []);
  };

  const prepareCartForCheckout = (): void => {
    const { brandConfig } = useBrandStore();
    setCartStatus(events.UPDATE);

    const {
      selectedLocations,
      sendOrderReceipt,
      getDeliveryFormsRequestData,
      getAllActiveDeliveryIdsInOrder,
      getDeliveryAddress,
      getExpressDeliveryAddress,
    } = useDeliveryStore();
    const { checkoutCart } = useOrderStore();

    const cleanedDeliveryMethods = getDeliveryFormsRequestData
      .filter((d) => getAllActiveDeliveryIdsInOrder.includes(d.deliveryId as DeliveryId))
      .map((d) => {
        let temp: CleanedDeliveryMethod;
        if (d.deliveryId === DELIVERY_ID) {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { validForDelivery, preselected, id, customerId, phone, ...rest } = getDeliveryAddress;
          temp = { ...d, ...rest, deliveryId: d.deliveryId };
        } else if (d.deliveryId === DELIVERY_EXPRESS) {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { validForDelivery, preselected, id, customerId, phone, ...rest } = getExpressDeliveryAddress;
          temp = { ...d, ...rest, deliveryId: d.deliveryId };
        } else {
          temp = { ...d, deliveryId: d.deliveryId as string };
        }

        (Object.keys(temp) as Array<keyof typeof temp>).forEach((key) => !temp[key] && delete temp[key]);
        return temp;
      });

    const orderDetails: OrderDeliveryDetails = {};
    cleanedDeliveryMethods.forEach((method) => {
      const { deliveryId, ...rest } = method;
      orderDetails[deliveryId] = rest;

      if (STORE_DELIVERIES.includes(deliveryId)) {
        orderDetails[deliveryId].storeId = selectedLocations[deliveryId as DeliveryId];
      }
      if (EXPRESS_DELIVERIES.includes(deliveryId)) {
        orderDetails[deliveryId].warehouseId = selectedLocations[deliveryId as DeliveryId];
      }
    });

    if (brandConfig.additions.orderReceipt) {
      orderDetails.sendOrderReceipt = sendOrderReceipt;
    }

    checkoutCart({ cartId: cartId.value!, orderDetails });
  };

  const showErrorMessage = (error: string): void => {
    const { showMessage } = useUiStore();
    const { $i18n } = useNuxtApp();
    showMessage({ text: `${$i18n.t('errors.occured')} ${error} ${$i18n.t('errors.try_again_later')}` });
  };

  const getCartIsMutating = computed<boolean>(() => cartStatus.value === statuses.mutating);

  const getCartIsEmpty = computed<boolean>(() => hasLoadedInitialData.value && cartItems.value.length === 0);

  const getTotalQuantity = computed<number>(() =>
    cartItems.value.reduce((acc: number, cartItem: CartItem) => formatFloat(acc + Number(cartItem.quantity)), 0),
  );

  const getPrices = computed<Price[]>(() => cartItems.value.map(({ price }) => price));

  const getItemsWithDeliveryId = (id: string) =>
    computed<CartItem[]>(() => {
      return cartItems.value.filter(({ deliveryId }) => deliveryId === id);
    });

  const getActiveCartItems = computed<CartItem[]>(() => {
    const { getAllActiveDeliveryIdsInOrder } = useDeliveryStore();
    return cartItems.value.filter((item) => getAllActiveDeliveryIdsInOrder.includes(item.deliveryId));
  });

  const getCartItemsObject = computed<{ [key: string]: CartItem }>(() =>
    cartItems.value.reduce((items: { [key: string]: CartItem }, item): { [key: string]: CartItem } => {
      items[item.cartItemId] = item;
      return items;
    }, {}),
  );

  const getRequiredActionsForCartItem = (
    cartItem: CartItem & {
      stock?: Stock;
      purchaseConstraints: PurchaseConstraints[];
      discontinued?: boolean;
      filterTags?: string[];
    },
  ) =>
    computed<RequiresChange>(() => {
      const { selectedLocations, getAllActiveDeliveryIdsInOrder, getAvailableDeliveryIds } = storeToRefs(
        useDeliveryStore(),
      );
      const { centralStorageId } = storeToRefs(useStoresStore());
      const { deliveryId, filterTags = [], quantity: cartQuantity, stock, discontinued } = cartItem;
      const actionsRequired: RequiresChange = {
        quantity: false,
        delivery: false,
        variantMissing: false,
        needsReplacement: false,
        notPurchasableStore: false,
        notPurchasableOnline: false,
      };
      if (getAllActiveDeliveryIdsInOrder.value.includes(deliveryId)) {
        const useCentralStorage = STANDARD_DELIVERIES.includes(deliveryId) && centralStorageId.value;
        const currentLocation = useCentralStorage ? centralStorageId.value : selectedLocations.value[deliveryId];
        const currentStock = stock?.locations?.find(
          (loc: { locationId: string }) => loc.locationId === currentLocation,
        );
        const stockedQuantity = currentStock?.quantity;

        const isPurchasable = isPurchasableOnline(cartItem, currentLocation);
        if (!cartItem.variantSlug) actionsRequired.variantMissing = true;
        if (!isPurchasable.online) actionsRequired.notPurchasableOnline = true;
        if (!isPurchasable.store) actionsRequired.notPurchasableStore = true;
        if (discontinued && (stockedQuantity === undefined || cartQuantity > stockedQuantity))
          actionsRequired.needsReplacement = true;
        if (deliveryId === DELIVERY_EXPRESS)
          actionsRequired.delivery = !(filterTags.includes('express-delivery') && stockedQuantity);
        else if (deliveryId === STORE_EXPRESS) actionsRequired.delivery = !stockedQuantity;
        if (EXPRESS_DELIVERIES.includes(deliveryId) && !actionsRequired.delivery)
          actionsRequired.quantity = cartQuantity > (stockedQuantity || 0);
        if (!getAvailableDeliveryIds.value.includes(deliveryId)) actionsRequired.delivery = true;
      }

      return actionsRequired;
    });

  const getSomeCartItemsRequiresActions = computed<boolean>(() => {
    if (!getActiveCartItems.value.length) return false;

    return getActiveCartItems.value.some((item) => {
      const actionsRequired = getRequiredActionsForCartItem(item).value;
      return (
        actionsRequired.quantity ||
        actionsRequired.delivery ||
        actionsRequired.needsReplacement ||
        actionsRequired.notPurchasableOnline ||
        actionsRequired.notPurchasableStore ||
        actionsRequired.variantMissing
      );
    });
  });

  return {
    cartId,
    setCartId,
    cartItems,
    setCartStatus,
    setCartItems,
    setQuickOrderVariants,
    quickOrderVariants,
    hasLoadedInitialData,
    rectifyDeliveryDate,
    updateRequestedDeliveryDate,
    transferCart,
    fetchCart,
    fetchVariants,
    fetchCartWithStockProductPrice,
    addItemToCart,
    addItemsToCart,
    itemsAddedNotification,
    loadXLSfile,
    getXLSX,
    removeSelectedItems,
    removeItem,
    updateItem,
    updateItems,
    prepareCartForCheckout,
    getCartItemsObject,
    getCartIsMutating,
    getCartIsEmpty,
    getTotalQuantity,
    getPrices,
    getItemsWithDeliveryId,
    getActiveCartItems,
    getRequiredActionsForCartItem,
    getSomeCartItemsRequiresActions,
  };
});
